import { useState } from 'react';
import { X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { ICaptionsPanelProps } from '@/@types/sceneEditorV2';
import type { CaptionPosition } from '@/@types/captions';
import { CAPTION_BORDER_COLOR_PRESETS } from '@/@types/captions';

function CaptionsPanel({ onClose, captionSettings, onUpdateCaptionSettings }: ICaptionsPanelProps) {
  const [customColor, setCustomColor] = useState('');

  const handleCustomColorApply = () => {
    const hex = customColor.startsWith('#') ? customColor : `#${customColor}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      onUpdateCaptionSettings({ borderColor: hex });
      setCustomColor('');
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-base font-medium">Captions</span>
        <button
          onClick={onClose}
          className="rounded-md p-0.5 text-foreground-muted transition-colors hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-4 p-3">
          {/* Section 1: Enable toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="captions-toggle" className="text-sm font-medium">
              Show captions
            </Label>
            <Switch
              id="captions-toggle"
              checked={captionSettings.enabled}
              onCheckedChange={checked => onUpdateCaptionSettings({ enabled: checked })}
            />
          </div>

          {captionSettings.enabled && (
            <>
              {/* Section 2: Border Color */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Border color</Label>
                <div className="flex flex-wrap gap-2">
                  {CAPTION_BORDER_COLOR_PRESETS.map(color => (
                    <button
                      key={color}
                      onClick={() => onUpdateCaptionSettings({ borderColor: color })}
                      className={cn(
                        'size-7 rounded-full border-2 transition-all',
                        captionSettings.borderColor === color
                          ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                          : 'border-border hover:scale-110'
                      )}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}

                  {/* Custom color picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className={cn(
                          'flex size-7 items-center justify-center rounded-full border-2 border-dashed border-border',
                          'text-xs font-bold text-foreground-muted transition-all hover:scale-110 hover:border-foreground-muted'
                        )}
                      >
                        +
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48" align="start">
                      <div className="flex flex-col gap-2">
                        <Label className="text-xs">Custom hex color</Label>
                        <div className="flex gap-1">
                          <Input
                            value={customColor}
                            onChange={e => setCustomColor(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCustomColorApply()}
                            placeholder="#FF00FF"
                            className="h-7 text-xs"
                          />
                          <button
                            onClick={handleCustomColorApply}
                            className="rounded-md bg-primary px-2 text-xs text-primary-foreground"
                          >
                            OK
                          </button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Section 3: Position */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Position</Label>
                <Select
                  value={captionSettings.position}
                  onValueChange={(val: CaptionPosition) =>
                    onUpdateCaptionSettings({ position: val })
                  }
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="top">Top</SelectItem>
                    <SelectItem value="middle">Middle</SelectItem>
                    <SelectItem value="bottom">Bottom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Section 4: Preview */}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Preview</Label>
                <div className="flex items-center justify-center rounded-lg bg-black/80 px-4 py-3">
                  <span
                    className="text-center text-base font-medium text-white"
                    style={{
                      WebkitTextStroke: `1.5px ${captionSettings.borderColor}`,
                      paintOrder: 'stroke fill',
                    }}
                  >
                    Sample{' '}
                    <span className="font-bold" style={{ fontSize: '120%' }}>
                      caption
                    </span>{' '}
                    text
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export { CaptionsPanel };
